import { loadImage, createCanvas } from "canvas";
import type { Bitmap, WritableBitmap } from "./types.ts";
import { bitmapChannels } from "./types.ts";
import { imageDataToBitmap } from "./image-data.ts";
import { uint8ArrayAlloc } from "../utils/uint8-array.ts";
import { disposeCanvasContext } from "../utils/canvas.ts";

function createBlankWritableBitmap(
	width: number,
	height: number,
	rgbOrRgba: number | undefined,
): WritableBitmap {
	return {
		data: uint8ArrayAlloc(width * height * bitmapChannels, rgbOrRgba),
		width,
		height,
	};
}

function createBlankBitmap(
	width: number,
	height: number,
	rgbOrRgba: number,
): Bitmap {
	return createBlankWritableBitmap(width, height, rgbOrRgba);
}

async function loadBitmapFromSource(source: Parameters<typeof loadImage>[0]) {
	const img = await loadImage(source, { crossOrigin: "anonymous" });
	const canvas = createCanvas(img.naturalWidth, img.naturalHeight);
	const ctx = canvas.getContext("2d");
	ctx.drawImage(img, 0, 0);
	const imageData = ctx.getImageData(
		0,
		0,
		img.naturalWidth,
		img.naturalHeight,
	) as ImageData;
	// In browsers this is a canvas at the image's full native resolution. WebKit
	// keeps a detached canvas's backing store until GC and caps total canvas
	// memory per page, so release it as soon as the pixels are copied out.
	disposeCanvasContext(ctx);
	return imageDataToBitmap(imageData);
}

async function loadBitmapFromUrl(url: string) {
	return loadBitmapFromSource(url);
}

export {
	loadBitmapFromSource,
	loadBitmapFromUrl,
	createBlankWritableBitmap,
	createBlankBitmap,
};
